import * as React from "react";
import { withOpenState, OpenStateInjectedProps } from "ui/common/enhancers/with-open-state";
import { ModalWrapper } from "app/components/modal-wrapper/modal-wrapper";
import { ModalSize } from "app/components/constants";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./case-view-modal.scss";
import { routeConstants, routeName } from "app/routes/route-constants";
import { removeCaseDraftSearchParams, inStartupDashboard } from "app/components/helpers";

interface CaseViewModalProps {
  children?: any;
  favouriteId?: number;
  dashboardType?: number;
  bowtieCaseId?: number;
  onClosedCallback?: () => void;
  clearSearchParamsOnClose?: boolean;
}

export type CaseViewModalComponentProps = CaseViewModalProps & OpenStateInjectedProps;

export const CaseViewModalComponent = (props: CaseViewModalComponentProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const scrollPositionRef = React.useRef(0);
  const previousPathRef = React.useRef<string>("");

  const status = searchParams.get("status");
  const modalKey = status ? `change-${status}` : 'no-change';
  const isCaseDraft = location.pathname.includes(`/${routeName.Case}/${routeName.Draft}/`);
  
  const connectingNewCaseFromExistingCase = isCaseDraft && !!Number(searchParams.get("connectionTypeId")) && 
    !!Number(searchParams.getAll("draftId")?.map(Number)?.some(n => Number.isFinite(n) && n > 0));
  const connectingNewCaseReverse = isCaseDraft && !!Number(searchParams.get("connectionTypeId")) && 
    !!Number(searchParams.getAll("caseId")?.map(Number)?.some(n => Number.isFinite(n) && n > 0));

  React.useEffect(() => {
    if (!props.isOpen) {
      props.open();

      // Save scroll position
      scrollPositionRef.current = window.scrollY;

      // Save the path of the page that opened the modal
      previousPathRef.current = location.pathname;

      // Lock background scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
    }
    return () => {
      // Clean up styles if unmounted
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      // Restore scroll only if we're still on the page that opened the modal
      if (location.pathname === previousPathRef.current) {
        window.scrollTo({ top: scrollPositionRef.current, behavior: 'instant' });
      }
    };
  }, []);

  React.useEffect(() => {
  if (status) {
    searchParams.delete("status");
  }
}, [status]);

  const getOriginatorPathReports = () => {
    if (props.dashboardType) {
      return routeConstants.dashboardWithFavouriteFor(props.dashboardType, props.favouriteId);
    }
    if (props.bowtieCaseId) {
      return routeConstants.bowtieViewFor(props.bowtieCaseId);
    }
    if (props.favouriteId) {
      return routeConstants.favouriteViewFor(Number(props.favouriteId));
    }
    return routeConstants.startupView;
  };

  const getOriginatorPathCaseViewModal = (caseId: number) => {
    if (props.dashboardType) {
      return routeConstants.dashboardWithFavouriteOpenCaseFor(props.dashboardType, props.favouriteId, caseId);
    }
    if (props.bowtieCaseId) {
      return routeConstants.bowtieViewOpenCaseFor(props.bowtieCaseId, caseId);
    }
    if (props.favouriteId) {
      return routeConstants.favouriteViewOpenCaseFor(Number(props.favouriteId), caseId);
    }
    return routeConstants.startupDashboardOpenCaseFor(caseId);
  };

  const closeModal = () => {
    let originatorPath = getOriginatorPathReports();
    removeCaseDraftSearchParams(searchParams);
    const currentSearchParams = inStartupDashboard() || !!props.clearSearchParamsOnClose ? '' : `?${searchParams?.toString()}`;   
    if (!isCaseDraft) {
      props.close(props.onClosedCallback);
      
      // Restore scroll position
      const y = scrollPositionRef.current;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo({ top: y, behavior: 'instant' });
    }
    if(connectingNewCaseReverse) {
      originatorPath = getOriginatorPathCaseViewModal(Number(searchParams.get("caseId")));
    }
    navigate(`${originatorPath}${currentSearchParams}`);
  };

  const modalView = React.useMemo(
    () => (
      <ModalWrapper onClose={closeModal} size={ModalSize.xtraLarge} hideCloseButton={connectingNewCaseFromExistingCase}>
        <div className={styles.caseViewModalContent}>{props.children}</div>
      </ModalWrapper>
    ),
    [props.children]
  );

  return props.isOpen ? (
    <div id="caseViewModal" className={styles.caseViewModal} key={modalKey}>
      {modalView}
    </div>
  ) : null;
};

export const CaseViewModal = withOpenState()(CaseViewModalComponent);
